export {};

declare var validateDiscountCode: any;

import {
  ModelManager,
  getSafeStorage,
  getStatsigClient,
  getCartItemsIdsString,
  getOrCreateStatsigManager,
  buildModelRequestPayload,
  shouldSendPayload,
  PayloadField,
} from './statsig-manager.js';

interface modelResponse {
  discount_type: string[];
  discount_value: number;
  force_login: boolean;
  product_handles: string[];
}

const MODEL_KEY = "statsig:dyo:promos4u_3";
const PROMOTION_COUPON_STORAGE_KEY = "insider:cart:promotionCoupon";
const EXPERIMENT = {
  title: "Promos4U",
  name: "dyo-promos4u",
  gate: "dyo-promos4u",
  layer: "dyo-promos4u",
  allocated_experiment: "",
};

type PromotionStatus = "blocked" | "allowed" | "pending";

interface PromotionCouponStorage {
  code?: string;
  discount?: string;
  force?: boolean;
  data?: { discount?: unknown };
  promos4u_preview?: boolean;
  discounts_cupons?: {
    discount_codes?: Record<string, unknown>;
  };
  discount_config?: unknown;
  discount_default?: unknown;
  oldCart?: unknown;
  disable_default_message?: boolean;
}

if (!customElements.get("promos-4u"))
  customElements.define(
    "promos-4u",
    class Promos4U extends HTMLElement {
      customerId?: string;
      statsigGate: string;
      statsigLayer: string;

      statsigStableId: string | null = null;
      allocatedExperiment: string | null = null;
      listCoupon: string | null = null;
      coupon: string | null = null;
      fallbackElement: HTMLElement | null = null;
      modelUrl: string | null = null;
      REQUEST_TIMEOUT_MS = 10000;
      PROMOTION_WAIT_TIMEOUT_MS = 5000;
      PROMOTION_POLL_INTERVAL_MS = 100;
      PROMOTION_RETRY_WINDOW_MS = 15000;
      PROMOTION_RETRY_INTERVAL_MS = 250;
      hasStartedStatsig = false;

      modelManager = new ModelManager();

      obgExperimentInfos: {
        stable_id: string;
        allocated_experiment: string;
        gate: string;
        layer: string;
        cart: Promise<string>;
        customer_id: string | null;
        category: string;
        component: string;
      };

      constructor() {
        super();
        this.customerId = this.getAttribute("data-customer-id") || null;
        this.statsigGate = this.getAttribute("data-statsig-gate") || EXPERIMENT.gate;
        this.statsigLayer = this.getAttribute("data-statsig-layer") || EXPERIMENT.layer;
        this.listCoupon = this.querySelector("#list").textContent || null;
        this.coupon = null;
        this.fallbackElement = null;

        this.obgExperimentInfos = {
          stable_id: this.statsigStableId,
          gate: this.statsigGate,
          layer: this.statsigLayer,
          cart: getCartItemsIdsString(),
          customer_id: this.customerId,
          allocated_experiment: EXPERIMENT.allocated_experiment,
          category: "Statsig",
          component: EXPERIMENT.name,
        };

        this.debugLog("constructor", {
          customerId: this.customerId,
          statsigGate: this.statsigGate,
          statsigLayer: this.statsigLayer,
        });
      }

      debugLog(_message: string, _payload?: unknown): void {}

      debugPromotionStatus(_status: PromotionStatus, _reason: string, _payload?: unknown): void {}

      async connectedCallback(): Promise<void> {
        try {
          this.debugLog("connectedCallback:start");
          const promotionStatus = await this.waitForPromotionStatus();
          this.debugLog("connectedCallback:resolved promotion status", { promotionStatus });

          if (promotionStatus === "blocked") return;

          if (promotionStatus === "pending") {
            this.retryStartWhenPromotionReady();
            return;
          }

          await this.startStatsig();
        } catch (error) {
          console.log("Error initializing Statsig manager:", error);
        }
      }

      retryStartWhenPromotionReady(): void {
        const startAt = Date.now();
        this.debugLog("retryStartWhenPromotionReady:start", {
          retryWindowMs: this.PROMOTION_RETRY_WINDOW_MS,
          retryIntervalMs: this.PROMOTION_RETRY_INTERVAL_MS,
        });

        const attemptStart = async () => {
          const status = this.verifyPromotion();

          if (status === "blocked") return;

          if (status === "allowed") {
            this.debugLog("retryStartWhenPromotionReady:status allowed, trying startStatsig");
            try {
              await this.startStatsig();
            } catch (error) {
              console.log("Error in retryStartWhenPromotionReady:", error);
            }
            return;
          }

          if (Date.now() - startAt >= this.PROMOTION_RETRY_WINDOW_MS) {
            this.debugLog("retryStartWhenPromotionReady:timeout window reached");
            return;
          }

          window.setTimeout(() => {
            void attemptStart();
          }, this.PROMOTION_RETRY_INTERVAL_MS);
        };

        void attemptStart();
      }

      async waitForPromotionStatus(): Promise<PromotionStatus> {
        const immediateStatus = this.verifyPromotion();
        this.debugLog("waitForPromotionStatus:immediate", { immediateStatus });

        if (immediateStatus !== "pending") {
          return immediateStatus;
        }

        return new Promise((resolve) => {
          let settled = false;

          const settle = (status: PromotionStatus) => {
            if (settled) return;

            settled = true;
            window.removeEventListener("PromotionCouponReady", onPromotionReady);
            clearInterval(pollInterval);
            clearTimeout(timeoutHandle);
            this.debugLog("waitForPromotionStatus:settle", { status });
            resolve(status);
          };

          const checkStatus = () => {
            const status = this.verifyPromotion();

            if (status !== "pending") {
              settle(status);
            }
          };

          const onPromotionReady = () => {
            this.debugLog("waitForPromotionStatus:event PromotionCouponReady");
            checkStatus();
          };

          const pollInterval = window.setInterval(
            checkStatus,
            this.PROMOTION_POLL_INTERVAL_MS
          );

          const timeoutHandle = window.setTimeout(() => {
            this.debugLog("waitForPromotionStatus:timeout reached");
            settle(this.verifyPromotion());
          }, this.PROMOTION_WAIT_TIMEOUT_MS);

          window.addEventListener("PromotionCouponReady", onPromotionReady, { once: true });
        });
      }

      async startStatsig(): Promise<void> {
        if (this.hasStartedStatsig) {
          this.debugLog("startStatsig:already started");
          return;
        }

        const promotionStatus = this.verifyPromotion();
        if (promotionStatus !== "allowed") {
          this.debugLog("startStatsig:blocked by promotion status", { promotionStatus });
          return;
        }

        this.hasStartedStatsig = true;
        this.debugLog("startStatsig:initializing manager");
        await getOrCreateStatsigManager();
        this.debugLog("startStatsig:running test");
        await this.runTest();
      }

      async persistForcedPromos4UCoupon(discountValue: number): Promise<void> {
        const couponCode = (this.coupon || "").trim().toUpperCase();
        if (!couponCode) return;

        const currentStorage = this.getPromotionCouponFromStorage() || {};
        const currentStorageCode = this.getEffectivePromotionCouponCode(currentStorage);

        const hasSameForcedCoupon =
          currentStorageCode === couponCode && currentStorage.force === true;
        if (hasSameForcedCoupon) {
          this.debugLog("persistForcedPromos4UCoupon:already persisted", {
            couponCode,
          });
          return;
        }

        let validatedCouponData: any = null;
        if (typeof validateDiscountCode === "function") {
          try {
            validatedCouponData = await validateDiscountCode(couponCode);
          } catch (error) {
            this.debugLog("persistForcedPromos4UCoupon:validate failed", {
              couponCode,
              error,
            });
          }
        }

        const fallbackDiscountData = {
          discount: {
            allocation_method: "across",
            target_selection: "all",
            target_type: "line_item",
            value: String(Math.round(discountValue * 100)),
            value_type: "percentage",
            allocation_limit: null,
            entitled_product_ids: [],
            entitled_variant_ids: [],
            entitled_collection_ids: [],
            usage_count: 0,
            usage_limit: null,
            changed_value: false,
            code: couponCode,
          },
        };

        const resolvedDiscountPayload =
          validatedCouponData?.discount || validatedCouponData?.data?.discount
            ? {
                discount:
                  validatedCouponData?.discount || validatedCouponData?.data?.discount,
              }
            : fallbackDiscountData;

        const payload: PromotionCouponStorage = {
          ...currentStorage,
          code: couponCode,
          force: true,
          promos4u_preview: true,
          data: resolvedDiscountPayload,
        };

        if (validatedCouponData?.discount_default) {
          payload.discount_default = validatedCouponData.discount_default;
        }

        if (typeof validatedCouponData?.disable_default_message !== "undefined") {
          payload.disable_default_message = validatedCouponData.disable_default_message;
        }

        if (validatedCouponData?.discount_config) {
          payload.discount_config = validatedCouponData.discount_config;
        }

        sessionStorage.setItem(PROMOTION_COUPON_STORAGE_KEY, JSON.stringify(payload));
        window.dispatchEvent(new CustomEvent("PromotionCouponReady"));

        this.debugLog("persistForcedPromos4UCoupon:stored", {
          couponCode,
          usedValidatedPayload: !!validatedCouponData,
        });
      }

      getEffectivePromotionCouponCode(promotionCoupon?: {
        code?: string;
        discount?: string;
        data?: { discount?: string | { code?: string } | Record<string, unknown> };
      } | null): string {
        const normalizedCode =
          promotionCoupon?.code ||
          (typeof promotionCoupon?.discount === "string" ? promotionCoupon.discount : null) ||
          (typeof promotionCoupon?.data?.discount === "string"
            ? promotionCoupon.data.discount
            : typeof promotionCoupon?.data?.discount === "object" && promotionCoupon.data.discount !== null
              ? (promotionCoupon.data.discount as { code?: string }).code
              : null) ||
          "";

        return normalizedCode.trim().toUpperCase();
      }

      cleanupPromo4uUI(): void {
        document.querySelector(".promos4u-info")?.remove();
        document.querySelector(".btn-open-coupon")?.remove();

        const login = document.querySelector(".popup-login") as HTMLElement | null;
        if (login) {
          login.classList.add("hidden");
          login.remove();
        }
      }

      verifyPromotion(): PromotionStatus {
        const SESSION_STORAGE = "insider:cart:promotionCoupon";
        const listCouponBlocked = this.getAttribute("data-list-blocked-coupons");

        if (!listCouponBlocked) {
          this.debugPromotionStatus("allowed", "no blocked coupons configured");
          return "allowed";
        }

        const normalize = (value?: string | null) =>
          (value || "").trim().toUpperCase();

        const couponsBlocked = listCouponBlocked
          .split(",")
          .map((c) => normalize(c))
          .filter(Boolean);

        const identifierFallback =
          this.getAttribute("data-identifier-fallback") || "collection-for-you";

        const fallbackElement = document.getElementById(identifierFallback);
        this.fallbackElement = fallbackElement;

        const raw = sessionStorage.getItem(SESSION_STORAGE);

        if (!raw) {
          this.debugPromotionStatus("pending", "sessionStorage empty", {
            sessionKey: SESSION_STORAGE,
          });
          return "pending";
        }

        let promotionCoupon: {
          code?: string;
          discount?: string;
          data?: { discount?: string | { code?: string } };
        } | null = null;

        try {
          promotionCoupon = JSON.parse(raw);
        } catch (error) {
          console.warn("[verifyPromotion] erro ao fazer parse do coupon:", raw, error);
          this.debugPromotionStatus("allowed", "invalid JSON in sessionStorage", { raw });
          return "allowed";
        }

        const couponCurrent = this.getEffectivePromotionCouponCode(promotionCoupon);
        const isBlocked = !!couponCurrent && couponsBlocked.includes(couponCurrent);

        if (isBlocked) {
          this.cleanupPromo4uUI();

          if (fallbackElement) {
            fallbackElement.style.display = "block";
          }

          this.style.display = "none";
          this.debugPromotionStatus("blocked", "coupon in blocked list", {
            couponCurrent,
            couponsBlocked,
          });
          return "blocked";
        }

        if (fallbackElement) {
          fallbackElement.style.display = "none";
        }

        this.debugPromotionStatus("allowed", "coupon not blocked", {
          couponCurrent,
        });
        return "allowed";
      }

      async runTest(): Promise<void> {
        try {
          if (this.verifyPromotion() === "blocked") {
            this.debugLog("runTest:aborted because promotion is blocked");
            return;
          }

          const stableContext = await getStatsigClient()?.getAsyncContext();
          this.statsigStableId = stableContext?.stableID ?? null;
          this.debugLog("runTest:stable context", { stableId: this.statsigStableId });

          const persistedModelData = this.loadModelFromStorage();
          if (persistedModelData) {
            this.debugLog("runTest:using persisted model data");
            this.applyLayoutChanges(persistedModelData);
            return;
          }

          const shouldSend = shouldSendPayload(["first_load", "stable_id"], MODEL_KEY, "every");
          if (!shouldSend) {
            this.debugLog("runTest:shouldSendPayload false -> fallback");
            this.activeFallback();
            return;
          }

          const experimentManager = await getOrCreateStatsigManager();
          if (!(await experimentManager.checkGate(this.statsigGate))) {
            this.debugLog("runTest:gate closed -> fallback", { gate: this.statsigGate });
            this.activeFallback();
            return;
          }

          const layer = await experimentManager.getLayer(this.statsigLayer);
          if (!layer) {
            this.debugLog("runTest:layer missing -> fallback", { layer: this.statsigLayer });
            this.activeFallback();
            return;
          }

          const allocatedExperiment = layer.__value.allocatedExperiment || EXPERIMENT.allocated_experiment;
          const normalizedAllocatedExperiment = (allocatedExperiment || "").replace(/\s+/g, "");
          const isHoldoutVariant = /(?:^|-)holdout(?:-|$)|(?:^|-)control(?:-|$)/i.test(
            normalizedAllocatedExperiment
          );
          const isKnownTreatmentVariant =
            /(?:^|-)random(?:-|$)/i.test(normalizedAllocatedExperiment) ||
            /(?:^|-)embedding-v1(?:-|$)/i.test(normalizedAllocatedExperiment);
          this.debugLog("runTest:allocated experiment", {
            allocatedExperiment,
            normalizedAllocatedExperiment,
            isHoldoutVariant,
            isKnownTreatmentVariant,
          });

          this.modelUrl = layer.get("modelUrl");
          if (!this.modelUrl){
            this.debugLog("runTest:modelUrl missing -> fallback");
            this.activeFallback();
            return;
          };

          const includeFields: PayloadField[] = [
            "customer_id",
            "stable_id",
            "allocated_experiment",
            "cart",
            "utms",
            "products",
            "collections",
          ];

          const payload = await buildModelRequestPayload(
            {
              customer_id: this.customerId || null,
              stable_id: this.statsigStableId || undefined,
              allocated_experiment: normalizedAllocatedExperiment || undefined,
            },
            includeFields
          );

          const modelData = (await this.modelManager.getModel(
            this.modelUrl,
            payload,
            { timeout: this.REQUEST_TIMEOUT_MS },
            MODEL_KEY,
            includeFields
          )) as modelResponse;

          this.debugLog("runTest:model response received", {
            hasModelData: !!modelData,
            discountValue: modelData?.discount_value,
            productCount: modelData?.product_handles?.length,
            forceLogin: modelData?.force_login,
          });

          if (isHoldoutVariant) {
            this.debugLog("runTest:allocated experiment marked as holdout/control -> fallback", {
              allocatedExperiment,
              normalizedAllocatedExperiment,
            });
            this.activeFallback();
          } else {
            this.debugLog("runTest:applying layout changes");
            this.applyLayoutChanges(modelData);
          }
        } catch (error) {
          console.log("ERROR in Statsig:", error);
        }
      }

      activeFallback(): void {
        this.debugLog("activeFallback:triggered");
        this.cleanupPromo4uUI();

        if (this.fallbackElement) {
          this.fallbackElement.style.display = "block";
        }

        this.style.display = "none";
        document.querySelector(".promos-4u")?.remove();
      }
      
      async applyLayoutChanges(response: modelResponse): Promise<void> {
        this.debugLog("applyLayoutChanges:start", {
          hasResponse: !!response,
          discountValue: response?.discount_value,
          productCount: response?.product_handles?.length,
          forceLogin: response?.force_login,
        });

        if (this.verifyPromotion() === "blocked") {
          this.debugLog("applyLayoutChanges:aborted because promotion is blocked");
          return;
        }

        if (!response || !response.product_handles || response.product_handles.length === 0) {
          this.debugLog("applyLayoutChanges:missing product handles -> fallback");
          this.activeFallback();
          return;
        };

        const productGrid = document.querySelector("#product-grid");
        if (!productGrid) {
          this.debugLog("applyLayoutChanges:product grid not found -> fallback");
          this.activeFallback();
          return;
        }

        const isGuest = this.customerId ? false : true;
        const hasCouponDiscount = response.discount_value > 0;
        const forceLogin = (isGuest) ? response.force_login : false;
        const urls = response.product_handles.map(h => `/products/${h}`);

        const productCards = await this.fetchProductCards(urls);
        if (!productCards) {
          this.debugLog("applyLayoutChanges:failed to fetch product cards -> fallback");
          this.activeFallback();
          return;
        }

        this.debugLog("applyLayoutChanges:product cards fetched", {
          count: productCards.length,
        });

        this.coupon = hasCouponDiscount
          ? this.getCouponByDiscountValue(response.discount_value)
          : null;

        this.debugLog("applyLayoutChanges:coupon resolved from discount", {
          coupon: this.coupon,
          hasCouponDiscount,
        });

        if (hasCouponDiscount && this.coupon) {
          this.clearStalePromos4UPreviewCoupon();
        }

        productGrid.innerHTML = "";

        productCards.forEach((card, index) => {
          const li = this.createCardElement(card);
          if (!li) return;

          this.handleGuestCard(li, forceLogin);
          this.setupPriceComponent(li, response.discount_value);
          this.setCardPosition(li, index);

          productGrid.appendChild(li);
        });

        const login = document.querySelector(".popup-login");

        if (forceLogin && login) {
          login.classList.remove("hidden");
        }

        if (hasCouponDiscount && (!forceLogin || !isGuest || (forceLogin && !isGuest))) {
          this.debugLog("applyLayoutChanges:calling handleLoggedUserCoupon", {
            forceLogin,
            isGuest,
          });
          await this.handleLoggedUserCoupon(response);
        }
      }

      createCardElement(card: string): HTMLElement | null {
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          `<li class="grid__item">${card}</li>`,
          "text/html"
        );
        return doc.body.firstElementChild as HTMLElement;
      }

      handleGuestCard(li: HTMLElement, isGuest: boolean): void {
        if (isGuest) {
          this.lockGuestCard(li);
        }
      }

      setupPriceComponent(li: HTMLElement, discountValue: number): void {
        if (discountValue <= 0) return;

        const priceEl = li.querySelector("price-normalize");
        if (!priceEl) return;
        if (!this.coupon) return;

        // Keep coupon hidden until explicit activation in the popup.
        priceEl.setAttribute("data-is-to-show-coupon-discount", "false");
        priceEl.setAttribute("data-hide-coupon-message", "true");
        priceEl.setAttribute("data-default-discount-code", this.coupon);
        priceEl.setAttribute("data-timestamp", `promos4u:preview-coupon-${Date.now()}`);
      }

      clearStalePromos4UPreviewCoupon(): void {
        const recommendedCoupon = (this.coupon || "").trim().toUpperCase();
        if (!recommendedCoupon) return;

        const storedPromotionCoupon = this.getPromotionCouponFromStorage();
        if (!storedPromotionCoupon || storedPromotionCoupon.promos4u_preview !== true) return;

        const storedCoupon = this.getEffectivePromotionCouponCode(storedPromotionCoupon);
        if (storedCoupon !== recommendedCoupon) return;

        const sanitized: PromotionCouponStorage = {
          ...storedPromotionCoupon,
          promos4u_preview: false,
          force: false,
        };

        if ((sanitized.code || "").trim().toUpperCase() === recommendedCoupon) {
          delete sanitized.code;
        }

        if ((sanitized.discount || "").trim().toUpperCase() === recommendedCoupon) {
          delete sanitized.discount;
        }

        const rawDiscountData = sanitized.data?.discount;
        const dataCode =
          typeof rawDiscountData === "string"
            ? rawDiscountData
            : typeof rawDiscountData === "object" && rawDiscountData !== null
              ? (rawDiscountData as { code?: string }).code || ""
              : "";

        if ((dataCode || "").trim().toUpperCase() === recommendedCoupon) {
          delete sanitized.data;
        }

        sessionStorage.setItem(PROMOTION_COUPON_STORAGE_KEY, JSON.stringify(sanitized));
        window.dispatchEvent(new CustomEvent("PromotionCouponReady"));

        this.debugLog("clearStalePromos4UPreviewCoupon:cleaned stale preview coupon", {
          recommendedCoupon,
        });
      }

      setCardPosition(li: HTMLElement, index: number): void {
        const cardEl = li.querySelector(".product-card-wrapper .card");
        if (cardEl) {
          cardEl.setAttribute("data-gtm-element-position", String(index + 1));
        }
      }

      getPromotionCouponFromStorage(): PromotionCouponStorage | null {
        const raw = sessionStorage.getItem(PROMOTION_COUPON_STORAGE_KEY);
        if (!raw) return null;

        try {
          return JSON.parse(raw);
        } catch (error) {
          console.warn("[promos4u] erro ao fazer parse do promotionCoupon:", raw, error);
          return null;
        }
      }

      isCurrentCouponAlreadyApplied(): boolean {
        const recommendedCoupon = (this.coupon || "").trim().toUpperCase();
        if (!recommendedCoupon) return false;

        try {
          const currentUrl = new URL(window.location.href);
          const couponFromQuery = (currentUrl.searchParams.get("cupom") || "").trim().toUpperCase();
          if (couponFromQuery && couponFromQuery === recommendedCoupon) {
            return true;
          }
        } catch {
          // Ignore malformed URL edge cases.
        }

        const storedPromotionCoupon = this.getPromotionCouponFromStorage();
        const storedCoupon = this.getEffectivePromotionCouponCode(storedPromotionCoupon);

        if (storedPromotionCoupon?.promos4u_preview === true && storedCoupon === recommendedCoupon) {
          return false;
        }

        return !!storedCoupon && storedCoupon === recommendedCoupon;
      }

      async handleLoggedUserCoupon(response: modelResponse): Promise<void> {
        if (!this.listCoupon) return;

        if (this.isCurrentCouponAlreadyApplied()) {
          this.debugLog("handleLoggedUserCoupon:coupon already applied, skipping popup", {
            coupon: this.coupon,
          });
          this.cleanupPromo4uUI();
          return;
        }

        this.debugLog("handleLoggedUserCoupon:start", {
          discountValue: response.discount_value,
        });

        const shouldShowBanner = () => {
          this.showCouponBanner(response.discount_value);

          this.debugLog("handleLoggedUserCoupon:banner check", {
            hasPopup: !!document.querySelector(".promos4u-info"),
            hasFloatingButton: !!document.querySelector(".btn-open-coupon"),
          });

          return !!document.querySelector(".promos4u-info") || !!document.querySelector(".btn-open-coupon");
        };

        if (shouldShowBanner()) return;

        const retryStartAt = Date.now();
        const retryWindowMs = this.PROMOTION_RETRY_WINDOW_MS;
        const retryIntervalMs = this.PROMOTION_RETRY_INTERVAL_MS;

        let retryTimeoutId: number | null = null;
        let done = false;

        const cleanup = () => {
          if (done) return;
          done = true;
          window.removeEventListener("PromotionCouponReady", onPromotionReady);
          if (retryTimeoutId !== null) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
          }
        };

        const tryRenderPopup = () => {
          if (done) return;

          const status = this.verifyPromotion();
          if (status === "blocked") {
            this.debugLog("handleLoggedUserCoupon:retry stopped because status is blocked");
            cleanup();
            return;
          }

          if (shouldShowBanner()) {
            this.debugLog("handleLoggedUserCoupon:retry succeeded, popup rendered");
            cleanup();
            return;
          }

          if (Date.now() - retryStartAt >= retryWindowMs) {
            this.debugLog("handleLoggedUserCoupon:retry timeout without popup", {
              retryWindowMs,
            });
            cleanup();
            return;
          }

          retryTimeoutId = window.setTimeout(tryRenderPopup, retryIntervalMs);
        };

        const onPromotionReady = () => {
          tryRenderPopup();
        };

        window.addEventListener("PromotionCouponReady", onPromotionReady);
        tryRenderPopup();
      }

      showCouponBanner(discountValue: number): void {
        if (discountValue <= 0) {
          this.debugLog("showCouponBanner:skipped because discount is zero", { discountValue });
          return;
        }

        if (this.isCurrentCouponAlreadyApplied()) {
          this.debugLog("showCouponBanner:coupon already applied, not rendering", {
            coupon: this.coupon,
          });
          return;
        }
 
        if (this.verifyPromotion() === "blocked") {
          this.debugLog("showCouponBanner:aborted because promotion is blocked");
          return;
        }
        if (document.querySelector(".promos4u-info") || document.querySelector(".btn-open-coupon")) {
          this.debugLog("showCouponBanner:already rendered, skipping duplicate");
          return;
        }

        this.debugLog("showCouponBanner:rendering", { discountValue });

        const discount = discountValue * 100;

        const applyCoupon = this.getAttribute("data-apply-coupon-text") || "Aplicar cupom";
        const imagePopupCoupon = this.getAttribute("data-image-popup-coupon") || null;
        const titleCoupon = this.getAttribute("data-title-coupon") || "Cupom disponível!";
        const textGetCoupon = this.getAttribute("data-text-get-coupon") || "Obter cupom";
        const removeCoupon = this.getAttribute("data-remove-coupon-text") || "Não quero cupom";
        const textCoupon = this.getAttribute("data-text-coupon")?.replace("|COUPON|", String(discount)) || "";

        const template = `
          <div class="promos4u-info">
            <div class="promos4u-info-text">
              <div class="image-coupon">
                ${imagePopupCoupon ? `<img src="${imagePopupCoupon}"/>` : ""}
              </div>
              <div class="container">
                <h3>${titleCoupon}</h3>
                <p>${textCoupon}</p>
                <button class="promos4u-apply-btn button" type="button">
                  <span>${applyCoupon}</span>
                  <div class="loading-overlay hidden">
                    <div class="loading-overlay__spinner">
                      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                      </svg>
                    </div>
                  </div>
                </button>
                <button class="promos4u-remove-btn button" type="button">${removeCoupon}</button>
              </div>
            </div>
          </div>
          <span class="btn-flutuante btn-open-coupon">${textGetCoupon}</span>
        `;

        document.body.insertAdjacentHTML("beforeend", template);

        const btnOpenCoupon = document.querySelector(".btn-open-coupon") as HTMLElement | null;
        const initialPopupCoupon = document.querySelector(".promos4u-info") as HTMLElement | null;

        if (initialPopupCoupon) {
          initialPopupCoupon.style.display = "flex";
          this.debugLog("showCouponBanner:popup opened immediately");
        }

        if (btnOpenCoupon) {
          btnOpenCoupon.style.display = "none";
        }

        btnOpenCoupon?.addEventListener("click", () => {
          if (this.verifyPromotion() === "blocked") return;

          const popupCoupon = document.querySelector(".promos4u-info") as HTMLElement | null;
          if (popupCoupon) popupCoupon.style.display = "flex";
          if (btnOpenCoupon) btnOpenCoupon.style.display = "none";
        });

        document
          .querySelector(".promos4u-apply-btn")
          ?.addEventListener("click", async () => await this.showApplyingCoupon());

        document
          .querySelector(".promos4u-remove-btn")
          ?.addEventListener("click", () => {
            const promoInfo = document.querySelector(".promos4u-info") as HTMLElement | null;
            if (promoInfo) promoInfo.style.display = "none";
            if (btnOpenCoupon) btnOpenCoupon.style.display = "block";
          });
      }
      getCouponByDiscountValue(value: number) {
        const couponsList = JSON.parse(this.listCoupon) as Record<string, { discount: number }>;
        const percentage = value * 100;

        for (const code in couponsList) {
          if (Object.prototype.hasOwnProperty.call(couponsList, code) && couponsList[code].discount === percentage) {
            return code;
          }
        }

        return null;
      }

      async showApplyingCoupon() {
        const BoxInfo = document.querySelector('.promos4u-info-text') as HTMLElement;
        const btnApply = BoxInfo.querySelector('.promos4u-apply-btn') as HTMLElement;
        const loadingOverlay = btnApply.querySelector('.loading-overlay') as HTMLElement;
        const removeBtn = BoxInfo.querySelector(".promos4u-remove-btn") as HTMLElement;
        const textBtn = btnApply.querySelector('span') as HTMLElement;
        const text = BoxInfo.querySelector('p') as HTMLElement;

        text.textContent = 'Aplicando cupom...';
        removeBtn.remove();
        textBtn.classList.add('hidden');
        loadingOverlay.classList.remove('hidden');

        try {
          await this.applyCouponDiscounts();
        } catch (error) {
          console.log("Error applying coupon:", error);
          text.textContent = 'Erro ao aplicar o cupom. Tente novamente.';
          textBtn.classList.remove('hidden');
        } finally {
          loadingOverlay.classList.add('hidden');
        }
      }

      async applyCouponDiscounts() {
        if (!this.coupon) return;

        try {
          const storedPromotionCoupon = this.getPromotionCouponFromStorage();
          if (storedPromotionCoupon?.promos4u_preview) {
            sessionStorage.setItem(
              PROMOTION_COUPON_STORAGE_KEY,
              JSON.stringify({
                ...storedPromotionCoupon,
                promos4u_preview: false,
              })
            );
          }

          await this.insertCouponInCheckout(this.coupon);
          const link = window.location.href;
          const url = new URL(link);
          window.location.href = url.origin + url.pathname + '?force=1&cupom=' + this.coupon;
        } catch (error) {
          console.log("Error applying coupon:", error);
        }
      }

      async insertCouponInCheckout(promotionCoupon : string) {
        const discountApplyUrl = `/discount/${promotionCoupon}?v=${Date.now()}`;
        await fetch(discountApplyUrl);
      }

      lockGuestCard(el: HTMLElement) {
        const quickAdd = el.querySelector("product-quick-add");
        if (quickAdd) quickAdd.remove();

        const btnQuickAdd = el.querySelector("button.product-quick-add__btn");
        if (btnQuickAdd) btnQuickAdd.remove();

        el.querySelectorAll(".card-link").forEach(link => {
          const anchor = link as HTMLAnchorElement;
          anchor.href = "#";
          anchor.addEventListener("click", (e) => {
            e.preventDefault();

            const btn = document.querySelector(".btn-open-popup-login") as HTMLElement;
            const popup = document.querySelector('.popup-login') as HTMLElement;
            if(popup) popup.style.display = 'flex';
            if (btn) btn.style.display = 'none';
            return false;
          });
        });

        const priceContent = el.querySelector(".card__content .card-information price-normalize");
        if (priceContent) {
          priceContent.classList.add("unlocked");
          priceContent.insertAdjacentHTML(
            "beforeend",
            `<div class="unlocked-info"><p>Desbloqueie fazendo o login</p></div>`
          );
        }
      }

      async fetchProductCards(urls: string[]) {
        try {
          const results = await Promise.all(
            urls.map(async (url) => {
              const res = await fetch(url + (url.includes("?") ? "&" : "?") + "view=promoCollectionForYou");
              return res.ok ? res.text() : null;
            })
          );
          return results.filter(Boolean) as string[];
        } catch (error) {
          console.log(`[Statsig][${EXPERIMENT.name}] Failed to fetch product cards`, error);
          return null;
        }
      }

      private loadModelFromStorage(): modelResponse | null {
        const storage = getSafeStorage();
        try {
          const raw = storage.getItem(MODEL_KEY);
          const parsed = raw ? JSON.parse(raw) : null;
          return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
          return null;
        }
      }
    }
  );
